<template>
	<v-row class="full-height position-of-letters-page">
		<v-col cols="12" class="flex-none pb-0">
			<v-card class="position-of-letters-toolbar-card" flat>
				<v-card-text class="pa-3">
					<v-row align="center" dense>
						<!-- Search -->
						<v-col cols="12" sm="6" md="4" lg="3">
							<v-text-field
								v-model="searchForm.keywords"
								placeholder="Search position of letters..."
								prepend-inner-icon="mdi-magnify"
								outlined
								dense
								hide-details
								clearable
								class="position-of-letters-search"
								@input="onSearchInput"
								@click:clear="onSearchClear"
							></v-text-field>
						</v-col>

						<!-- Spacer -->
						<v-spacer class="d-none d-md-flex"></v-spacer>

						<!-- New Position Button -->
						<v-col cols="auto" v-if="canManageLetters()">
							<v-btn
								depressed
								rounded
								color="success"
								@click="newLetter()"
								:disabled="activeEdit !== null"
							>
								New Position Of Letters
							</v-btn>
						</v-col>
					</v-row>
				</v-card-text>
			</v-card>
		</v-col>

		<v-col cols="12" class="flex-auto pt-0 position-of-letters-table-container">
			<v-data-table
				:headers="headers"
				:items="letters"
				:loading="loading"
				:options.sync="tableOptions"
				:server-items-length="totalCount"
				:footer-props="footerProps"
				:mobile-breakpoint="0"
				class="position-of-letters-data-table"
				must-sort
				fixed-header
			>
				<!-- Code Column -->
				<template v-slot:item.code="{ item }">
					<template v-if="item === activeEdit">
						<v-text-field
							outlined
							dense
							hide-details="auto"
							v-model="item.code"
							:disabled="!canManageLetters()"
						></v-text-field>
					</template>
					<template v-else>
						{{ item.code }}
					</template>
				</template>

				<!-- Description Column -->
				<template v-slot:item.description="{ item }">
					<template v-if="item === activeEdit">
						<v-text-field
							outlined
							dense
							hide-details="auto"
							v-model="item.description"
							:disabled="!canManageLetters()"
						></v-text-field>
					</template>
					<template v-else>
						{{ item.description }}
					</template>
				</template>

				<!-- Action Column -->
				<template v-slot:item.action="{ item }">
					<template v-if="item === activeEdit">
						<v-btn
							icon
							color="primary"
							@click="
								item.letterPositionId == null ? removeLetter() : (activeEdit = null);
								fetchData();
							"
							:disabled="!canManageLetters()"
						>
							<v-icon style="font-size: 2rem">mdi-close</v-icon>
						</v-btn>
						<v-btn
							:disabled="!canManageLetters()"
							icon
							color="primary"
							@click="saveLetter()"
						>
							<v-icon style="font-size: 2rem">mdi-check</v-icon>
						</v-btn>
					</template>
					<template v-else>
						<v-btn text color="primary" @click.stop="activeEdit = item">
							Edit
							<v-icon style="font-size: 1.4rem">mdi-arrow-right</v-icon>
						</v-btn>
					</template>
				</template>

				<!-- Loading slot -->
				<template v-slot:loading>
					<v-skeleton-loader type="table-row@10"></v-skeleton-loader>
				</template>

				<!-- No data slot -->
				<template v-slot:no-data>
					<div class="text-center pa-4">
						<v-icon large color="grey lighten-1">mdi-format-letter-case</v-icon>
						<p class="mt-2 grey--text">No position of letters found</p>
					</div>
				</template>
			</v-data-table>
		</v-col>

		<WarningModal
			v-if="deleteWarning"
			:showModal="deleteWarning"
			:message="'Are you sure you want to delete the selected position of letters?'"
			:itemList="deleteLetters"
			:itemListKey="'name'"
			:cancelText="'No, Cancel'"
			:confirmText="'Yes, Delete'"
			@cancel="deleteWarning = false"
			@confirm="
				deleteLetter();
				deleteWarning = false;
			"
		/>
	</v-row>
</template>

<script>
import gql from "graphql-tag";
import WarningModal from "@/components/WarningModal.vue";
import { hasPermission, SystemPermissions } from "@/models/User";
import { getApiUrl } from "@/helpers";

export default {
	name: "PositionOfLetters",
	components: {
		WarningModal,
	},
	data() {
		return {
			loading: true,
			searchForm: {
				keywords: "",
			},
			letters: [],
			totalCount: 0,
			deleteLetters: [],
			deleteWarning: false,
			activeEdit: null,
			searchTimeout: null,
			tableOptions: {
				page: 1,
				itemsPerPage: 15,
				sortBy: ["code"],
				sortDesc: [false],
			},
			footerProps: {
				"items-per-page-options": [10, 15, 25, 50],
				"show-first-last-page": true,
			},
			headers: [
				{
					text: "Code",
					value: "code",
					sortable: true,
					width: "20%",
				},
				{
					text: "Description",
					value: "description",
					sortable: true,
					width: "60%",
				},
				{
					text: "",
					value: "action",
					sortable: false,
					width: "20%",
					align: "end",
				},
			],
		};
	},
	computed: {
		navigationDirection() {
			return this.$store.getters["globalState/navigationDirection"];
		},
	},
	watch: {
		tableOptions: {
			handler() {
				this.fetchData();
			},
			deep: true,
		},
		navigationDirection: {
			handler(newDirection) {
				if (newDirection) {
					const savedState = this.$store.getters["globalState/positionOfLettersState"];
					if (savedState) {
						this.tableOptions.page = Math.floor(savedState.offset / 15) + 1;
						this.searchForm.keywords = savedState.keywords || "";
						if (savedState.sortBy) {
							this.tableOptions.sortBy = [savedState.sortBy];
						}
						if (savedState.sortDesc !== undefined) {
							this.tableOptions.sortDesc = [savedState.sortDesc];
						}

						this.$nextTick(() => {
							if (typeof savedState.scrollPosition === "number") {
								setTimeout(() => {
									window.scrollTo({
										top: savedState.scrollPosition,
										left: 0,
										behavior: "smooth",
									});
								}, 500);
							}
						});
					}
				}
			},
			immediate: true,
		},
	},
	methods: {
		canManageLetters() {
			const user = this.$store.getters.userInfo;
			if (hasPermission(user, SystemPermissions.Inventory_PositionOfLetters_Create_Update_Delete)) {
				return true;
			}
			return user.isAdmin;
		},

		// Map table column values to GraphQL sort field names
		getSortFieldName(columnValue) {
			const sortFieldMap = {
				code: "code",
				description: "description",
			};
			return sortFieldMap[columnValue] || "code";
		},

		buildQueryVariables() {
			const { page, itemsPerPage, sortBy, sortDesc } = this.tableOptions;

			// Build dynamic sort order
			const sortColumn = sortBy[0] || "code";
			const sortDirection = sortDesc[0] ? "DESC" : "ASC";
			const sortFieldName = this.getSortFieldName(sortColumn);
			const order = [{ [sortFieldName]: sortDirection }];

			return {
				offset: (page - 1) * itemsPerPage,
				take: itemsPerPage,
				order,
			};
		},

		async fetchData() {
			this.loading = true;
			try {
				const variables = this.buildQueryVariables();
				const res = await this.$apollo.query({
					query: gql`
						query GetLetterPosition(
							$offset: Int!
							$take: Int!
							$order: [LetterPositionSortInput!] = [{ code: ASC }]
						) {
							letterPosition(skip: $offset, take: $take, order: $order) {
								totalCount
								items {
									letterPositionId
									code
									description
								}
								pageInfo {
									hasNextPage
									hasPreviousPage
								}
							}
						}
					`,
					variables,
					fetchPolicy: "no-cache",
				});

				this.totalCount = res.data.letterPosition.totalCount;
				this.letters = res.data.letterPosition.items;
				this.loading = false;
			} catch (error) {
				console.error("Error fetching letter positions:", error);
				this.loading = false;
			}
		},

		onSearchInput() {
			// Debounced search
			if (this.searchTimeout) {
				clearTimeout(this.searchTimeout);
			}
			this.searchTimeout = setTimeout(() => {
				this.tableOptions.page = 1;
				this.fetchData();
			}, 400);
		},

		onSearchClear() {
			if (this.searchTimeout) {
				clearTimeout(this.searchTimeout);
			}
			this.tableOptions.page = 1;
			this.fetchData();
		},

		newLetter() {
			const newLetter = {
				letterPositionId: null,
				code: "",
				description: "",
			};

			this.letters.unshift(newLetter);

			//set the new letter as the active edit
			this.activeEdit = newLetter;
		},

		selectLetter(data) {
			//add or remove the selected letter to the delete list
			if (this.deleteLetters.some((obj) => obj == data)) {
				this.deleteLetters = this.deleteLetters.filter((obj) => obj !== data);
			} else {
				this.deleteLetters.push(data);
			}
		},

		removeLetter() {
			this.letters.splice(0, 1);
			this.activeEdit = null;
		},

		async deleteLetter() {
			try {
				//loop through all deleteLetter and delete them by id
				for await (const l of this.deleteLetters) {
					const res = await this.$http.delete(
						getApiUrl(`/api/settings/letter-position/${l.letterPositionId}`)
					);
					console.log(res);
				}
				console.log("refreshing");

				//refresh latest letter positions and reset deleteLetters
				this.fetchData();
				this.deleteLetters = [];
			} catch (error) {
				console.log(error);
			}
		},

		async saveLetter() {
			try {
				let res;
				if (this.activeEdit.letterPositionId) {
					res = await this.$http.put(
						getApiUrl("/api/settings/letter-position"),
						this.activeEdit
					);
				} else {
					res = await this.$http.post(
						getApiUrl("/api/settings/letter-position"),
						this.activeEdit
					);
				}
				console.log(res);

				//refresh latest letters and clear activeEdit
				this.fetchData();
				this.activeEdit = null;
			} catch (error) {
				console.log(error);
			}
		},
	},
	mounted() {
		// Initial fetch will happen via tableOptions watcher
	},
	beforeDestroy() {
		if (this.searchTimeout) {
			clearTimeout(this.searchTimeout);
		}
	},
	beforeRouteLeave(to, from, next) {
		this.$store.dispatch("globalState/savePositionOfLettersState", {
			offset: (this.tableOptions.page - 1) * this.tableOptions.itemsPerPage,
			keywords: this.searchForm.keywords,
			sortBy: this.tableOptions.sortBy[0],
			sortDesc: this.tableOptions.sortDesc[0],
			scrollPosition: window.scrollY,
		});
		next();
	},
};
</script>

<style lang="scss" scoped>
.position-of-letters-page {
	height: calc(100vh - 88px);
	display: flex;
	flex-direction: column;
	overflow: hidden;
}

.position-of-letters-table-container {
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0;
	overflow: hidden;
}

.position-of-letters-toolbar-card {
	border-radius: 4px 4px 0 0 !important;
	border-bottom: 1px solid $color-gray;
	flex-shrink: 0;
}

.position-of-letters-search {
	::v-deep .v-input__slot {
		background-color: $color-gray-light-2 !important;
	}
}

.position-of-letters-data-table {
	border-radius: 0 0 4px 4px !important;
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0;
	overflow: hidden;

	::v-deep .v-data-table__wrapper {
		flex: 1;
		overflow-y: auto;
		overflow-x: auto;
		table {
			min-width: 600px;
		}
	}

	::v-deep .v-data-footer {
		flex-shrink: 0;
	}

	::v-deep > .v-data-table__wrapper > table > thead > tr > th {
		font-size: 1.4rem !important;
		font-weight: 500 !important;
		color: #000 !important;
		text-transform: uppercase;
		background-color: $color-gray-light-2 !important;
		white-space: nowrap;
	}

	::v-deep > .v-data-table__wrapper > table > tbody > tr > td {
		height: 60px !important;
		font-size: 1.6rem !important;
	}

	::v-deep .v-data-footer {
		font-size: 1.25rem !important;
	}

	::v-deep .v-data-footer__select .v-select__selections .v-select__selection--comma {
		font-size: 1.15rem !important;
	}
}
</style>
